import { useEffect, useRef } from 'react';

// Window event fired by useGlobalNotificationsWs when a `draft_changed` WS
// frame arrives. Mirrors the existing `teamlio:draft-seen-changed` channel:
// the always-on sidebar WS hook is the single dispatcher, and every draft
// count/list consumer subscribes via useDraftRefreshSignal below.
export const DRAFT_CHANGED_EVENT = 'teamlio:draft-changed';

// Throttle for the "window came back to the foreground" safety net only. The
// WS ping itself is never throttled (it only fires on a real change); this
// just stops rapid minimize/restore churn from re-hitting the API.
const VISIBILITY_REFETCH_THROTTLE_MS = 30_000;

/**
 * Refetch draft data in response to:
 *   1. the `draft_changed` WS ping — immediately, and
 *   2. the window becoming visible again — at most once per 30s.
 *
 * This replaces the old 60s `setInterval` polling. Nothing fires while the
 * window stays hidden/minimized, so steady-state idle costs zero requests.
 *
 * `cb` performs the refetch (the latest closure is always used). `enabled`
 * gates the subscription — when false, no listeners are attached.
 */
export function useDraftRefreshSignal(cb: () => void, enabled = true): void {
  const cbRef = useRef(cb);
  useEffect(() => {
    cbRef.current = cb;
  });

  const lastRunRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    const run = () => {
      lastRunRef.current = Date.now();
      cbRef.current();
    };

    const onPing = () => run();

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRunRef.current < VISIBILITY_REFETCH_THROTTLE_MS) return;
      run();
    };

    window.addEventListener(DRAFT_CHANGED_EVENT, onPing);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener(DRAFT_CHANGED_EVENT, onPing);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled]);
}
